import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Popconfirm, Tag, Select, message, Modal, Row, Col, Breadcrumb, InputNumber } from 'antd';
import { DeleteOutlined, EditOutlined, HomeOutlined, CheckOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api, { getUser } from '../services/auth';

const RecordHistory = ({ isMobile }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [records, setRecords] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const [editVisible, setEditVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editProjectId, setEditProjectId] = useState(null);
  const [editNodesMap, setEditNodesMap] = useState({});
  const [editNavPath, setEditNavPath] = useState([]);
  const [editCurrentNodes, setEditCurrentNodes] = useState([]);
  const [editTime, setEditTime] = useState('');
  const [editNumericValue, setEditNumericValue] = useState(null);
  const [editNumericUnit, setEditNumericUnit] = useState(null);

  const loadRecords = useCallback(async () => {
    const params = {};
    if (selectedProject) params.projectId = selectedProject;
    try {
      const res = await api.get('/records', { params });
      const data = res.data;
      if (Array.isArray(data)) {
        setRecords(data);
      } else {
        setRecords([]);
      }
    } catch (err) {
      console.error('加载记录失败:', err);
      message.error('加载记录失败: ' + (err.response?.data?.error || err.message));
      setRecords([]);
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data.filter(p => !p.is_template));
    } catch (err) {
      console.error('加载项目失败:', err);
    }
  };

  useEffect(() => {
    setCurrentUser(getUser());
    loadProjects();
    loadRecords();

    const onFocus = () => loadRecords();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadRecords]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleExport = () => {
    const data = records.map(r => ({
      '项目': r.project_name || '',
      '记录路径': r.node_path || '',
      '数值': r.numeric_value != null ? r.numeric_value : '',
      '单位': r.numeric_unit || '',
      '记录时间': r.recorded_at ? new Date(r.recorded_at).toLocaleString() : '',
      '记录人': r.username || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '记录历史');
    const colWidths = [
      { wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 8 }, { wch: 22 }, { wch: 10 },
    ];
    ws['!cols'] = colWidths;
    const projectName = selectedProject 
      ? projects.find(p => p.id === selectedProject)?.name || '全部' 
      : '全部';
    XLSX.writeFile(wb, `记录历史_${projectName}_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
    message.success('导出成功');
  };

  const handleDeleteRecord = async (id) => {
    try {
      await api.delete(`/records/${id}`);
      message.success('删除成功');
      loadRecords();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const formatDateTimeLocal = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEdit = async (record) => {
    setEditRecord(record);
    setEditTime(formatDateTimeLocal(record.recorded_at));
    setEditProjectId(record.project_id);
    setEditNumericValue(record.numeric_value);
    setEditNumericUnit(record.numeric_unit);

    try {
      const res = await api.get(`/projects/${record.project_id}/nodes`);
      setEditNodesMap({ [record.project_id]: res.data.tree });

      const pathNames = record.node_path.split('/');
      const navPathFromRecord = buildNavPathFromTree(res.data.tree, pathNames);
      setEditNavPath(navPathFromRecord);

      if (navPathFromRecord.length > 0) {
        const lastNode = navPathFromRecord[navPathFromRecord.length - 1];
        setEditCurrentNodes(lastNode.children || []);
      } else {
        setEditCurrentNodes(res.data.tree);
      }
    } catch (err) {
      console.error('加载节点失败:', err);
      setEditNavPath([]);
      setEditCurrentNodes([]);
    }

    setEditVisible(true);
  };

  const buildNavPathFromTree = (tree, pathNames) => {
    const result = [];
    let currentLevel = tree;

    for (const name of pathNames) {
      const found = currentLevel.find(n => n.name === name);
      if (!found) break;
      result.push(found);
      currentLevel = found.children || [];
    }

    return result;
  };

  const handleEditNodeClick = (node) => {
    if (node.children && node.children.length > 0) {
      setEditNavPath(prev => [...prev, node]);
      setEditCurrentNodes(node.children);
    } else {
      setEditNavPath(prev => [...prev, node]);
      setEditCurrentNodes([]);
    }
  };

  const handleEditBreadcrumbClick = (index) => {
    if (index === -1) {
      const tree = editNodesMap[editProjectId] || [];
      setEditNavPath([]);
      setEditCurrentNodes(tree);
    } else {
      const newPath = editNavPath.slice(0, index + 1);
      const node = newPath[newPath.length - 1];
      setEditNavPath(newPath);
      setEditCurrentNodes(node.children || []);
    }
  };

  const handleEditSubmit = async () => {
    const nodePath = editNavPath.length > 0
      ? editNavPath.map(n => n.name).join('/')
      : editRecord.node_path;

    let value = null;
    if (editNumericValue !== null && editNumericValue !== undefined) {
      value = editNumericUnit ? `${editNumericValue} ${editNumericUnit}` : editNumericValue;
    }

    try {
      await api.put(`/records/${editRecord.id}`, {
        nodePath,
        value,
        numericValue: editNumericValue,
        numericUnit: editNumericUnit,
        recordedAt: editTime ? editTime.replace('T', ' ') + ':00' : undefined,
      });
      message.success('修改成功');
      setEditVisible(false);
      setEditRecord(null);
      loadRecords();
    } catch (err) {
      message.error('修改失败');
    }
  };

  const formatTimeDiff = (ms) => {
    if (!ms || ms < 0) return '';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}秒`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}分钟`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}小时${minutes % 60}分钟`;
    const days = Math.floor(hours / 24);
    return `${days}天${hours % 24}小时`;
  };

  const getTimeDiff = (index) => {
    if (index >= records.length - 1) return null;
    const current = new Date(records[index].recorded_at);
    const next = new Date(records[index + 1].recorded_at);
    return current - next;
  };

  const isProjectOwner = (project) => {
    return project.user_id === currentUser?.id;
  };

  const editSelectedPath = editNavPath.map(n => n.name).join('/');
  const editHasChildren = editCurrentNodes.length > 0;

  const editBreadcrumbItems = [
    {
      key: 'root',
      title: <a href="#root" onClick={(e) => { e.preventDefault(); handleEditBreadcrumbClick(-1); }}><HomeOutlined /> 根节点</a>,
    },
    ...editNavPath.map((node, index) => ({
      key: node.id,
      title: index < editNavPath.length - 1 ? (
        <a href={`#nav-${node.id}`} onClick={(e) => { e.preventDefault(); handleEditBreadcrumbClick(index); }}>{node.name}</a>
      ) : (
        <span>{node.name}</span>
      ),
    }))
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Select
          style={{ width: isMobile ? '100%' : 300 }}
          placeholder="筛选项目（默认全部）"
          allowClear
          onChange={(val) => setSelectedProject(val || null)}
          options={projects.map(p => ({
            value: p.id,
            label: (
              <span>
                {p.name}
                {!isProjectOwner(p) && !p.is_template && <Tag color="green" style={{ marginLeft: 6, marginBottom: 2, fontSize: 10 }}>共享</Tag>}
              </span>
            ),
          }))}
        />
      </div>

      <Card 
        title={`记录历史 (${records.length})`}
        extra={
          records.length > 0 && (
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              size="small"
              onClick={handleExport}
              style={{ borderRadius: 10, fontWeight: 500 }}
            >
              导出Excel
            </Button>
          )
        }
        size={isMobile ? 'small' : 'medium'}
      >
        {records.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#86868b', padding: 24, fontSize: 15, fontWeight: 500 }}>暂无记录</div>
        ) : records.map((r, index) => {
            const diff = getTimeDiff(index);
            const isOwnRecord = r.user_id === currentUser?.id;
            return (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  gap: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: isMobile ? 13 : 14 }}>{r.node_path}</span>
                    {diff !== null && (
                      <Tag color="orange" style={{ fontSize: isMobile ? 10 : 12, margin: 0 }}>
                        间隔 {formatTimeDiff(diff)}
                      </Tag>
                    )}
                    {!isOwnRecord && (
                      <Tag color="blue" style={{ fontSize: isMobile ? 10 : 12, margin: 0 }}>
                        {r.username}
                      </Tag>
                    )}
                  </div>
                  <div style={{ fontSize: isMobile ? 11 : 13, color: '#86868b', marginTop: 4 }}>
                    {r.project_name} · {new Date(r.recorded_at).toLocaleString()}
                  </div>
                </div>
                {r.numeric_value !== null && r.numeric_value !== undefined && (
                  <Tag color="green">
                    {r.numeric_value} {r.numeric_unit || ''}
                  </Tag>
                )}
                {r.value && <Tag>{r.value}</Tag>}
                {isOwnRecord && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <Button type="text" size={isMobile ? 'small' : 'middle'} icon={<EditOutlined />} onClick={() => openEdit(r)} />
                    <Popconfirm title="确定删除？" onConfirm={() => handleDeleteRecord(r.id)}>
                      <Button type="text" danger size={isMobile ? 'small' : 'middle'} icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                )}
              </div>
            );
          })
        }
      </Card>

      <Modal
        title="修改记录"
        open={editVisible}
        onCancel={() => { setEditVisible(false); setEditRecord(null); }}
        footer={null}
        width={isMobile ? '95%' : 600}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>选择记录路径：</div>
          <Breadcrumb style={{ marginBottom: 8 }} items={editBreadcrumbItems} />
          {editSelectedPath && (
            <div style={{
              padding: '6px 10px',
              background: 'rgba(52,199,89,0.08)',
              border: '1px solid rgba(52,199,89,0.2)',
              borderRadius: 8,
              marginBottom: 8,
              fontSize: 13,
              color: '#248a3d',
              fontWeight: 500,
            }}>
              当前选择：{editSelectedPath}
            </div>
          )}
          {editHasChildren ? (
            <Row gutter={[8, 8]}>
              {editCurrentNodes.map((node) => (
                <Col span={8} key={node.id}>
                  <Button
                    type="primary"
                    size="middle"
                    block
                    onClick={() => handleEditNodeClick(node)}
                    style={{ borderRadius: 8 }}
                  >
                    {node.name}
                  </Button>
                </Col>
              ))}
            </Row>
          ) : (
            editNavPath.length > 0 && (
              <div style={{ textAlign: 'center', color: '#86868b', padding: 8, fontSize: 13 }}>
                已到达末级节点
              </div>
            )
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>数值（可选）：</div>
          <Row gutter={[8, 8]}>
            <Col xs={12}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="数值"
                value={editNumericValue}
                onChange={setEditNumericValue}
                step="0.01"
              />
            </Col>
            <Col xs={12}>
              <Select
                style={{ width: '100%' }}
                placeholder="单位"
                allowClear
                value={editNumericUnit}
                onChange={setEditNumericUnit}
                options={[
                  { value: '元', label: '元' },
                  { value: 'kg', label: 'kg' },
                  { value: 'g', label: 'g' },
                  { value: '分钟', label: '分钟' },
                  { value: '小时', label: '小时' },
                  { value: '次', label: '次' },
                  { value: '个', label: '个' },
                  { value: '件', label: '件' },
                  { value: '米', label: '米' },
                  { value: '公里', label: '公里' },
                  { value: '升', label: '升' },
                ]}
              />
            </Col>
          </Row>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>记录时间：</div>
          <input
            type="datetime-local"
            value={editTime}
            onChange={(e) => setEditTime(e.target.value)}
            style={{
              width: '100%',
              height: 40,
              padding: '4px 11px',
              fontSize: 14,
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 8,
              outline: 'none',
              background: 'rgba(255,255,255,0.8)',
            }}
          />
        </div>

        <Button
          type="primary"
          icon={<CheckOutlined />}
          size="large"
          block
          onClick={handleEditSubmit}
          style={{
            height: 44,
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 12,
            background: '#52c41a',
            borderColor: '#52c41a',
            boxShadow: '0 1px 3px rgba(52,199,89,0.3)',
          }}
        >
          保存修改
        </Button>
      </Modal>
    </div>
  );
};

export default RecordHistory;
